import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [token,   setToken]   = useState(() => localStorage.getItem('token'));
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  /* ── Persist token ──────────────────────────────────────────────── */
  const saveToken = useCallback((t) => {
    if (t) {
      localStorage.setItem('token', t);
    } else {
      localStorage.removeItem('token');
    }
    setToken(t);
  }, []);

  /* ── Verify token on mount / token change ───────────────────────── */
  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 401) throw new Error('session_expired');
        return r.ok ? r.json() : Promise.reject();
      })
      .then(data => setUser(data.user || data))
      .catch((err) => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Email / Password login ─────────────────────────────────────── */
  const login = async (email, password) => {
    setError(null);
    try {
      const r    = await fetch(`${API_URL}/api/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Login failed');

      saveToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Login failed';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  /* ── Email / Password register ──────────────────────────────────── */
  const register = async (email, password, name = '') => {
    setError(null);
    try {
      const r    = await fetch(`${API_URL}/api/auth/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim().toLowerCase(), password, name }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Registration failed');

      saveToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Registration failed';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  /* ── Google OAuth login / register ─────────────────────────────── */
  const googleLogin = async (credential) => {
    setError(null);
    try {
      const r    = await fetch(`${API_URL}/api/auth/google`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ credential }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Google sign-in failed');

      saveToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Google sign-in failed';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  /* ── Logout ─────────────────────────────────────────────────────── */
  const logout = useCallback(() => {
    saveToken(null);
    setUser(null);
    setError(null);
  }, [saveToken]);

  /* ── Clear error helper ─────────────────────────────────────────── */
  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        login,
        register,
        googleLogin,
        logout,
        clearError,
        API_URL,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
